/**
 * @file dispatch.service.ts
 * @description Dedicated service for delivery order querying, driver assignment, and status transitions using existing delivery_orders and orders tables.
 */

import { supabase } from '../../../config/supabase.config';
import type { DeliveryOrder, DeliveryOrderStatus } from '../../../types/dispatch.types';

export const fetchDeliveryOrders = async (venueId: string): Promise<DeliveryOrder[]> => {
  // Query existing delivery_orders joined with orders, couriers, and order items
  const { data, error } = await supabase
    .from('delivery_orders')
    .select(`
      id,
      order_id,
      venue_id,
      delivery_status,
      customer_name,
      customer_phone,
      delivery_address,
      created_at,
      assigned_courier_id,
      courier:couriers (
        id,
        full_name
      ),
      order:orders (
        id,
        total_amount,
        status,
        order_items (
          id,
          quantity,
          price,
          items (
            name
          )
        )
      )
    `)
    .eq('venue_id', venueId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[dispatch.service] Error querying delivery_orders, attempting orders fallback:', error);
    // Fallback directly to orders with type = delivery if delivery_orders has an error
    const { data: ordersData, error: ordersErr } = await supabase
      .from('orders')
      .select(`
        id,
        venue_id,
        status,
        total_amount,
        created_at,
        metadata,
        order_items (
          id,
          quantity,
          price,
          items (name)
        )
      `)
      .eq('venue_id', venueId)
      .eq('type', 'delivery')
      .order('created_at', { ascending: false });

    if (ordersErr) throw ordersErr;

    return (ordersData || []).map((row: any) => {
      const meta = row.metadata || {};
      return {
        id: row.id,
        venue_id: row.venue_id,
        order_number: `#${row.id.slice(-4).toUpperCase()}`,
        customer_name: meta.customer_name || 'Customer',
        customer_phone: meta.customer_phone || '',
        delivery_address: meta.customer_address || 'Address pending',
        total_amount: Number(row.total_amount) || 0,
        status: (row.status as DeliveryOrderStatus) || 'pending',
        courier_id: meta.courier_id || null,
        courier_name: meta.courier_name || null,
        created_at: row.created_at,
        items: (row.order_items || []).map((oi: any) => ({
          id: oi.id,
          name: oi.items?.name || 'Item',
          quantity: oi.quantity,
          price: Number(oi.price) || 0,
        })),
      };
    });
  }

  return (data || []).map((row: any) => {
    const ord = row.order || {};
    const items = (ord.order_items || []).map((oi: any) => ({
      id: oi.id,
      name: oi.items?.name || 'Item',
      quantity: oi.quantity,
      price: Number(oi.price) || 0,
    }));

    return {
      id: row.id,
      venue_id: row.venue_id,
      order_number: `#${(row.order_id || row.id).slice(-4).toUpperCase()}`,
      customer_name: row.customer_name || 'Customer',
      customer_phone: row.customer_phone || '',
      delivery_address: row.delivery_address || 'Address pending',
      total_amount: Number(ord.total_amount) || 0,
      status: (row.delivery_status as DeliveryOrderStatus) || 'pending',
      courier_id: row.assigned_courier_id || null,
      courier_name: row.courier?.full_name || null,
      created_at: row.created_at,
      items,
    };
  });
};

export const updateDeliveryStatus = async (
  deliveryOrderId: string,
  newStatus: DeliveryOrderStatus
): Promise<void> => {
  // Update existing delivery_orders table
  const { data: dRow } = await supabase
    .from('delivery_orders')
    .update({ delivery_status: newStatus })
    .eq('id', deliveryOrderId)
    .select('order_id')
    .single();

  // If connected to orders, also update order status
  if (dRow?.order_id) {
    await supabase
      .from('orders')
      .update({ status: newStatus })
      .eq('id', dRow.order_id);
  }
};
